import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, FormHelperText,
    FormLabel, Input, Modal, ModalDialog, Option, Select, Sheet, Stack,
    Switch, Table, Textarea, Typography,
} from '@mui/joy';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsAccountActivate, APIAdminAdsAccountAdjustBalance,
    APIAdminAdsAccountSetTrusted, APIAdminAdsAccountSuspend,
    APIAdminAdsAccounts, adminAdsError,
} from '../../../api_admin_ads';
import {
    ACCOUNT_STATUS_COLORS, ACCOUNT_STATUS_LABELS, formatMoney,
} from '../../../components/Ads/adsConstants';


function BalanceModal({ open, account, onClose, onDone }) {
    const { adminToken: token } = useAdmin();
    const [amount, setAmount] = useState('');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) { setAmount(''); setComment(''); setError(null); }
    }, [open]);

    const submit = async () => {
        const value = Number(amount);
        if (!value) { setError('Укажите сумму, отличную от нуля'); return; }
        if (!comment.trim()) { setError('Комментарий обязателен — он попадёт в аудит'); return; }
        try {
            setSaving(true);
            setError(null);
            await APIAdminAdsAccountAdjustBalance(token, account.id, {
                amount: value, comment: comment.trim(),
            });
            onDone(`Баланс аккаунта #${account.id} изменён на ${value} ₽`);
            onClose();
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={saving ? undefined : onClose}>
            <ModalDialog sx={{ minWidth: 380 }}>
                <Typography level="title-lg">Корректировка баланса</Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                    {account?.title} · текущий баланс {formatMoney(account?.balance)} ₽
                </Typography>

                {error && <Alert color="danger" sx={{ mb: 1.5 }}>{error}</Alert>}

                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Сумма, ₽</FormLabel>
                        <Input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="1000 или -1000"
                        />
                        <FormHelperText>
                            Положительная сумма — зачисление, отрицательная — списание
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Комментарий</FormLabel>
                        <Textarea
                            minRows={2}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Основание корректировки"
                        />
                        <FormHelperText>
                            Обязателен: попадёт в реестр транзакций и в лог действий администратора
                        </FormHelperText>
                    </FormControl>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="outlined" color="neutral" fullWidth onClick={onClose} disabled={saving}>
                            Отмена
                        </Button>
                        <Button fullWidth loading={saving} onClick={submit}>Применить</Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


function SuspendModal({ open, account, onClose, onDone }) {
    const { adminToken: token } = useAdmin();
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => { if (open) { setReason(''); setError(null); } }, [open]);

    const submit = async () => {
        if (!reason.trim()) { setError('Укажите причину — её увидит партнёр'); return; }
        try {
            setSaving(true);
            await APIAdminAdsAccountSuspend(token, account.id, reason.trim());
            onDone(`Аккаунт #${account.id} приостановлен`);
            onClose();
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={saving ? undefined : onClose}>
            <ModalDialog sx={{ minWidth: 380 }}>
                <Typography level="title-lg">Приостановить аккаунт</Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                    Все работающие кампании будут остановлены. При разблокировке
                    они вернутся в работу автоматически — те, что партнёр
                    останавливал сам, останутся на паузе.
                </Typography>

                {error && <Alert color="danger" sx={{ mb: 1.5 }}>{error}</Alert>}

                <Textarea
                    minRows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Причина приостановки"
                />

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button variant="outlined" color="neutral" fullWidth onClick={onClose} disabled={saving}>
                        Отмена
                    </Button>
                    <Button color="danger" fullWidth loading={saving} onClick={submit}>
                        Приостановить
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


export default function AdminAdsAccountsPage() {
    const { adminToken: token, has } = useAdmin();
    const canManage = has('ads.manage');
    const canBilling = has('ads.billing');

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [status, setStatus] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [busy, setBusy] = useState(null);
    const [balanceTarget, setBalanceTarget] = useState(null);
    const [suspendTarget, setSuspendTarget] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdminAdsAccounts(token, {
                status: status || undefined,
                search: search || undefined,
                page_size: 50,
            });
            setItems(res?.items || []);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить рекламодателей'));
        } finally {
            setLoading(false);
        }
    }, [token, status, search]);

    useEffect(() => { load(); }, [load]);

    const handleDone = (message) => { setNotice(message); load(); };

    const toggleTrusted = async (account) => {
        setBusy(`trusted-${account.id}`);
        try {
            await APIAdminAdsAccountSetTrusted(token, account.id, !account.is_trusted);
            await load();
        } catch (e) {
            setError(adminAdsError(e));
        } finally { setBusy(null); }
    };

    const activate = async (account) => {
        setBusy(`activate-${account.id}`);
        try {
            await APIAdminAdsAccountActivate(token, account.id);
            setNotice(`Аккаунт #${account.id} активирован`);
            await load();
        } catch (e) {
            setError(adminAdsError(e));
        } finally { setBusy(null); }
    };

    return (
        <Box>
            <Typography level="h4" sx={{ mb: 2 }}>Рекламодатели</Typography>

            {notice && <Alert color="success" sx={{ mb: 2 }}>{notice}</Alert>}
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Input
                        size="sm"
                        placeholder="Название, ИНН, партнёр"
                        startDecorator={<SearchRoundedIcon />}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ flex: 1, minWidth: 220 }}
                    />
                    <Select size="sm" value={status} onChange={(_, v) => setStatus(v ?? '')}
                            sx={{ minWidth: 180 }}>
                        <Option value="">Все статусы</Option>
                        <Option value="active">Активные</Option>
                        <Option value="suspended">Приостановленные</Option>
                        <Option value="blocked">Заблокированные</Option>
                    </Select>
                </Stack>
            </Sheet>

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>ID</th>
                                <th>Рекламодатель</th>
                                <th style={{ width: 130 }}>Статус</th>
                                <th style={{ width: 120 }}>Баланс</th>
                                <th style={{ width: 120 }}>Потрачено</th>
                                <th style={{ width: 110 }}>Кампании</th>
                                <th style={{ width: 130 }}>Автомодерация</th>
                                <th style={{ width: 240 }}>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((account) => (
                                <tr key={account.id}>
                                    <td>{account.id}</td>
                                    <td>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                            {account.title || '—'}
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                            {account.partner_name}
                                            {account.legal_inn ? ` · ИНН ${account.legal_inn}` : ''}
                                        </Typography>
                                        {account.suspend_reason && (
                                            <Typography level="body-xs" color="danger">
                                                {account.suspend_reason}
                                            </Typography>
                                        )}
                                    </td>
                                    <td>
                                        <Chip size="sm" variant="soft"
                                              color={ACCOUNT_STATUS_COLORS[account.status] || 'neutral'}>
                                            {ACCOUNT_STATUS_LABELS[account.status] || account.status}
                                        </Chip>
                                    </td>
                                    <td>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                            {formatMoney(account.balance)} ₽
                                        </Typography>
                                        {Number(account.hold_balance) > 0 && (
                                            <Typography level="body-xs" color="neutral">
                                                в резерве {formatMoney(account.hold_balance)} ₽
                                            </Typography>
                                        )}
                                    </td>
                                    <td>{formatMoney(account.total_spent)} ₽</td>
                                    <td>
                                        {account.campaigns_active} / {account.campaigns_total}
                                    </td>
                                    <td>
                                        <Switch
                                            checked={!!account.is_trusted}
                                            disabled={!canManage || busy === `trusted-${account.id}`}
                                            onChange={() => toggleTrusted(account)}
                                        />
                                    </td>
                                    <td>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {canBilling && (
                                                <Button size="sm" variant="soft"
                                                        onClick={() => setBalanceTarget(account)}>
                                                    Баланс
                                                </Button>
                                            )}
                                            {canManage && account.status === 'active' && (
                                                <Button size="sm" variant="soft" color="danger"
                                                        onClick={() => setSuspendTarget(account)}>
                                                    Приостановить
                                                </Button>
                                            )}
                                            {canManage && account.status !== 'active' && (
                                                <Button size="sm" variant="soft" color="success"
                                                        loading={busy === `activate-${account.id}`}
                                                        onClick={() => activate(account)}>
                                                    Активировать
                                                </Button>
                                            )}
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={8}>
                                        <Typography level="body-sm" color="neutral"
                                                    sx={{ py: 3, textAlign: 'center' }}>
                                            Рекламодателей не найдено
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            <BalanceModal
                open={!!balanceTarget}
                account={balanceTarget}
                onClose={() => setBalanceTarget(null)}
                onDone={handleDone}
            />
            <SuspendModal
                open={!!suspendTarget}
                account={suspendTarget}
                onClose={() => setSuspendTarget(null)}
                onDone={handleDone}
            />
        </Box>
    );
}